import React, { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { Link } from 'react-router-dom';
import env from './utils/env';

const SubscriptionSuccess: React.FC = () => {
    const { user, token } = useAuth();

    const [isLoading, setIsLoading] = useState(true);
    const [subscriptionStatus, setSubscriptionStatus] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);
    

    const apiBaseUrl = env.apiUrl;

    useEffect(() => {
        const checkSubscriptionStatus = async () => {
            if (!token) {
                setError('Authentication required');
                setIsLoading(false);
                return;
            }

            try {
                const response = await fetch(`${apiBaseUrl}/subscription/status`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data.error || 'Failed to fetch subscription status');
                }

                setSubscriptionStatus(data);
            } catch (err: any) {
                console.error('Error checking subscription status:', err);
                setError(err.message || 'Failed to verify subscription status');
            } finally {
                setIsLoading(false);
            }
        };

        // Small delay to allow webhook processing
        const timeoutId = setTimeout(checkSubscriptionStatus, 2000);
        return () => clearTimeout(timeoutId);
    }, [token, apiBaseUrl]);

    if (isLoading) {
        return (
            <div className="container">
                <div className="success-page">
                    <div className="loading-indicator">
                        <div className="spinner"></div>
                        <h2>Verifying your subscription...</h2>
                        <p>Please wait while we confirm your payment.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container">
                <div className="success-page">
                    <div className="error-state">
                        <span className="error-icon">⚠️</span>
                        <h2>Subscription Verification Error</h2>
                        <p>{error}</p>
                        <div className="action-buttons">
                            <Link to="/content-search" className="primary-button">
                                Continue to App
                            </Link>
                            <Link to="/subscription/manage" className="secondary-button">
                                Manage Subscription
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const isSuccessful = subscriptionStatus?.hasActiveSubscription || user?.role === 'USER';

    return (
        <div className="container">
            <div className="success-page">
                {isSuccessful ? (
                    <div className="success-state">
                        <span className="success-icon">🎉</span>
                        <h1>Welcome to Pro!</h1>
                        <p>Your subscription has been activated successfully.</p>
                        
                        <div className="subscription-details">
                            <h3>Subscription Details</h3>
                            <div className="detail-item">
                                <span className="label">Status:</span>
                                <span className="value success">{subscriptionStatus?.subscriptionStatus || 'Active'}</span>
                            </div>
                            {subscriptionStatus?.subscriptionCurrentPeriodEnd && (
                                <div className="detail-item">
                                    <span className="label">Next Billing Date:</span>
                                    <span className="value">
                                        {new Date(subscriptionStatus.subscriptionCurrentPeriodEnd).toLocaleDateString()}
                                    </span>
                                </div>
                            )}
                        </div>

                        <div className="benefits-reminder">
                            <h3>Your Pro Benefits</h3>
                            <ul className="benefits-list">
                                <li>✅ Unlimited searches</li>
                                <li>✅ Advanced analytics</li>
                                <li>✅ Priority support</li>
                                <li>✅ Export capabilities</li>
                                <li>✅ Full access to all features</li>
                            </ul>
                        </div>

                        <div className="action-buttons">
                            <Link to="/content-search" className="primary-button">
                                Start Using Pro Features
                            </Link>
                            <Link to="/subscription/manage" className="secondary-button">
                                Manage Subscription
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="pending-state">
                        <span className="pending-icon">⏳</span>
                        <h2>Payment Processing</h2>
                        <p>Your payment is being processed. This may take a few minutes.</p>
                        <p>You will receive an email confirmation once your subscription is activated.</p>
                        
                        <div className="action-buttons">
                            <Link to="/content-search" className="primary-button">
                                Continue to App
                            </Link>
                            <button 
                                onClick={() => window.location.reload()} 
                                className="secondary-button"
                            >
                                Refresh Status
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionSuccess; 